import React from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { DownloadIcon } from 'lucide-react';
import InsightCard from '../components/Dashboard/InsightCard';
import { cashFlowData } from '../utils/mockData';
const CashFlow = () => {
  // Format currency
  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString()}`;
  };
  // Prepare waterfall data for visualization
  const prepareWaterfallData = () => {
    let cumulative = 0;
    return cashFlowData.waterfall.map(item => {
      const start = cumulative;
      cumulative += item.value;
      const end = cumulative;
      return {
        ...item,
        start,
        end
      };
    });
  };
  const waterfallData = prepareWaterfallData();
  return <div data-id="element-883">
      <div className="mb-6 flex justify-between items-start" data-id="element-884">
        <div data-id="element-885">
          <h1 className="text-2xl font-bold text-gray-800" data-id="element-886">
            Flujo de Efectivo
          </h1>
          <p className="text-gray-600" data-id="element-887">
            Análisis detallado de entradas y salidas de efectivo
          </p>
        </div>
        <button className="flex items-center space-x-2 text-sm bg-blue-600 text-white px-4 py-2.5 rounded-md hover:bg-blue-700 transition-colors" data-id="element-888">
          <DownloadIcon size={18} data-id="element-889" />
          <span data-id="element-890">Exportar PDF</span>
        </button>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-891">
        {cashFlowData.summary.map((item, index) => <div key={item.name} className={`p-4 rounded-xl border shadow-sm ${index === 0 ? 'bg-blue-50 border-blue-200' : index === 1 ? 'bg-green-50 border-green-200' : index === 2 ? 'bg-red-50 border-red-200' : 'bg-purple-50 border-purple-200'}`} data-id="element-892">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-893">{item.name}</h3>
            <p className={`text-2xl font-bold mt-1 ${index === 0 ? 'text-blue-700' : index === 1 ? 'text-green-700' : index === 2 ? 'text-red-700' : 'text-purple-700'}`} data-id="element-894">
              {formatCurrency(item.value)}
            </p>
          </div>)}
      </div>
      {/* Waterfall Chart */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6" data-id="element-895">
        <h2 className="text-lg font-semibold mb-4" data-id="element-896">
          Flujo de Efectivo (Cascada)
        </h2>
        <div className="h-80" data-id="element-897">
          <ResponsiveContainer width="100%" height="100%" data-id="element-898">
            <BarChart data={waterfallData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-899">
              <CartesianGrid strokeDasharray="3 3" data-id="element-900" />
              <XAxis dataKey="name" data-id="element-901" />
              <YAxis tickFormatter={value => `$${(value / 1000).toFixed(0)}K`} data-id="element-902" />
              <Tooltip formatter={value => [formatCurrency(value as number), undefined]} labelFormatter={label => `${label}`} data-id="element-903" />
              <Legend data-id="element-904" />
              <ReferenceLine y={0} stroke="#000" data-id="element-905" />
              <Bar dataKey="value" fill={entry => {
              if (entry.name === 'Inicial' || entry.name === 'Final') return '#8884d8';
              return entry.value >= 0 ? '#4CAF50' : '#F44336';
            }} radius={[4, 4, 0, 0]} data-id="element-906" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Monthly Cash Flow Trends */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6" data-id="element-907">
        <h2 className="text-lg font-semibold mb-4" data-id="element-908">
          Tendencias de Flujo (Últimos 6 meses)
        </h2>
        <div className="h-80" data-id="element-909">
          <ResponsiveContainer width="100%" height="100%" data-id="element-910">
            <LineChart data={cashFlowData.monthly} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-911">
              <CartesianGrid strokeDasharray="3 3" data-id="element-912" />
              <XAxis dataKey="month" data-id="element-913" />
              <YAxis tickFormatter={value => `$${(value / 1000).toFixed(0)}K`} data-id="element-914" />
              <Tooltip formatter={value => [formatCurrency(value as number), undefined]} labelFormatter={label => `Mes: ${label}`} data-id="element-915" />
              <Legend data-id="element-916" />
              <Line type="monotone" dataKey="cashIn" name="Entradas" stroke="#4CAF50" strokeWidth={2} data-id="element-917" />
              <Line type="monotone" dataKey="cashOut" name="Salidas" stroke="#F44336" strokeWidth={2} data-id="element-918" />
              <Line type="monotone" dataKey="netCash" name="Flujo neto" stroke="#2196F3" strokeWidth={2} data-id="element-919" />
              <ReferenceLine y={0} stroke="#000" strokeDasharray="3 3" data-id="element-920" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Alerts Section */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-921">
        <h2 className="text-lg font-semibold mb-4" data-id="element-922">Alertas de Flujo</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-923">
          <InsightCard type="negative" message="Mes con flujo negativo por segundo mes consecutivo. Esto podría afectar tu liquidez a corto plazo." data-id="element-924" />
          <InsightCard type="warning" message="Mayor salida de dinero inesperada en inversiones ($78,325). Verifica si esto estaba presupuestado." data-id="element-925" />
          <InsightCard type="warning" message="Cuentas por cobrar (AR) aumentaron 22.1%. Revisa tu estrategia de cobranza." data-id="element-926" />
          <InsightCard type="info" message="Tu ciclo de conversión de efectivo es de 61 días. Considera estrategias para reducirlo." data-id="element-927" />
        </div>
      </div>
    </div>;
};
export default CashFlow;